var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../context/ConfigContext';
import { useEventApi } from '../../hooks/useEvent/useEventApi';
import { ComposerAnalyticsEvent } from '../../types/component';
import { transformSpaceAPIToSpaceType } from '../utils';
import { resolveSpacesAfterAdd } from '../../utils/resolveSpacesAfterAdd';
import { SpaceBookingWrapper } from '../SpaceBooking';
import { useEventComposerGlobalContext } from '../../composers/EventComposerGlobalContext';
/**
 * Internal implementation of SpaceBooking.
 * Assumes all required contexts (ApolloProvider, etc.) are already present.
 * This is the internal component that is used to render the SpaceBooking component in the EventComposer.
 */
export var SpaceBookingInternal = function (_a) {
    var open = _a.open, setSpaceBookingOpen = _a.setSpaceBookingOpen, onError = _a.onError;
    var t = useTranslation('spaceBooking').t;
    var _b = useConfig(), accessToken = _b.accessToken, environmentUrls = _b.environmentUrls, tenantId = _b.tenantId, userContext = _b.userContext;
    var currentOrg = userContext.currentOrg;
    var apiUrl = environmentUrls.apiUrl, baseApiUrl = environmentUrls.baseApiUrl;
    var getValues = useFormContext().getValues;
    var _c = useEventComposerGlobalContext(), setPendingSpaceSwap = _c.setPendingSpaceSwap, spacesState = _c.spacesState, setSpaces = _c.setSpaces, removePendingSpace = _c.removePendingSpace, initialSpaces = _c.initialSpaces, levelId = _c.levelId, locationId = _c.locationId, setLevelId = _c.setLevelId, setLocationId = _c.setLocationId, onTrackComposerEvent = _c.onTrackComposerEvent;
    var pendingSpaceSwap = spacesState.pendingSpaceSwap;
    var fetchSpace = useEventApi({
        accessToken: accessToken,
        apiUrl: apiUrl,
        baseApiUrl: baseApiUrl,
        tenantId: tenantId,
        currentOrg: currentOrg,
    }).fetchSpace;
    var timeValues = useMemo(function () {
        return {
            startTime: getValues('startTime'),
            endTime: getValues('endTime'),
        };
    }, [getValues]);
    /**
     * Space ids already on the event (excluding the one being swapped), used by
     * `useSpaceBookingLogic` to gate request-only spaces. The product rule is that
     * an event may have only one request-only space with no other spaces, so we only
     * block request-only when there are other (non-swapped) spaces present.
     *
     * The space being swapped is excluded from this list so a request-only replacement
     * can still be selected when swapping the only space on the event.
     */
    var spacesAdded = useMemo(function () {
        var _a;
        var existingSpaces = (_a = getValues('spaces')) !== null && _a !== void 0 ? _a : [];
        var spaceIds = existingSpaces.map(function (space) { return space.id.toString(); });
        if (pendingSpaceSwap) {
            return spaceIds.filter(function (id) { return id !== pendingSpaceSwap.spaceId.toString(); });
        }
        return spaceIds;
    }, [getValues, pendingSpaceSwap]);
    /** The space being swapped is always hidden from the picker via a separate list. */
    var spaceIdsToHide = useMemo(function () {
        if (!pendingSpaceSwap)
            return undefined;
        return [pendingSpaceSwap.spaceId.toString()];
    }, [pendingSpaceSwap]);
    var handleAddSpace = useCallback(function (spaceId, levelId, locationId) { return __awaiter(void 0, void 0, void 0, function () {
        var spaces, space, transformedSpace, newSpaces, error_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (levelId) {
                        setLevelId(levelId);
                    }
                    if (locationId) {
                        setLocationId(locationId);
                    }
                    spaces = (_a = getValues('spaces')) !== null && _a !== void 0 ? _a : [];
                    if (spaces.some(function (space) { return space.id.toString() === spaceId; })) {
                        onError(t('space_already_added'));
                        return [2 /*return*/];
                    }
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, fetchSpace({ spaceId: spaceId })];
                case 2:
                    space = _b.sent();
                    if (space) {
                        transformedSpace = transformSpaceAPIToSpaceType(space);
                        newSpaces = resolveSpacesAfterAdd(spaces, transformedSpace, initialSpaces);
                        setSpaces(newSpaces);
                        onTrackComposerEvent === null || onTrackComposerEvent === void 0 ? void 0 : onTrackComposerEvent(ComposerAnalyticsEvent.ADDED_SPACE, {
                            spaceId: spaceId,
                            isRequestOnly: !!transformedSpace.isManaged,
                            spaceCount: newSpaces.length,
                            wasSuggested: false,
                        });
                        if (pendingSpaceSwap) {
                            removePendingSpace(pendingSpaceSwap);
                        }
                        /* The suggested-space prompt hides itself once `spaces` is non-empty
                         * (see Spaces.tsx), so booking a space here clears it implicitly. */
                        setSpaceBookingOpen(false);
                    }
                    else {
                        onError(t('space_not_found'));
                    }
                    return [3 /*break*/, 4];
                case 3:
                    error_1 = _b.sent();
                    /* Throw error if unable to fetch space*/
                    onError(error_1 instanceof Error ? error_1.message : String(error_1));
                    throw error_1;
                case 4: return [2 /*return*/];
            }
        });
    }); }, [
        pendingSpaceSwap,
        setSpaces,
        initialSpaces,
        setSpaceBookingOpen,
        fetchSpace,
        onError,
        t,
        getValues,
        removePendingSpace,
        setLevelId,
        setLocationId,
        onTrackComposerEvent,
    ]);
    var handleClose = useCallback(function () {
        setSpaceBookingOpen(false);
        if (pendingSpaceSwap) {
            setPendingSpaceSwap(null);
        }
    }, [setSpaceBookingOpen, setPendingSpaceSwap, pendingSpaceSwap]);
    var startTime = timeValues.startTime, endTime = timeValues.endTime;
    return (_jsx(SpaceBookingWrapper, { levelId: levelId !== null && levelId !== void 0 ? levelId : undefined, locationId: locationId !== null && locationId !== void 0 ? locationId : undefined, startTime: startTime, endTime: endTime, open: open, onClose: handleClose, onAddSpace: handleAddSpace, spacesAdded: spacesAdded, spaceIdsToHide: spaceIdsToHide }));
};
